import React from "react";
import { motion } from "framer-motion";
import {
  Droplet, Filter, Wind, Disc, Sparkles, ClipboardList,
  Battery, Gauge, Wrench,
} from "lucide-react";
import { classes } from "./tokens";
import { useCarServicePackage } from "./CarServicePackageContext";

/**
 * "What's Included" — Bento Control-Room grid.
 * Items are pulled live from the CRM /service-packages endpoint and fall
 * back to a curated static list if the API is unreachable.
 */
/**
 * Fallback tile list — kept in sync with the CRM `/service-packages`
 * response for GENERAL Service. Never list an item we don't actually
 * deliver: previously included exterior wash + AC vent cleaning which
 * are NOT part of general service (those live in AC / DetailingWash
 * package categories on the CRM).
 */
const FALLBACK_ITEMS = [
  { name: "Engine Oil Replacement",     description: "OEM-grade synthetic / mineral oil suited to your engine spec." },
  { name: "Oil Filter Replacement",     description: "Brand-new OEM filter every service — no reuse." },
  { name: "Air Filter Replacement",     description: "Fresh air filter for better combustion and mileage." },
  { name: "Wiper Fluid Top Up",         description: "Streak-free washer fluid, refilled to max." },
  { name: "Coolant Top Up (200 ml)",    description: "Anti-freeze / anti-boil topped up per manufacturer spec." },
  { name: "Brake Fluid Top Up",         description: "Sealed hydraulic top-up. Better pedal feel." },
  { name: "Front Brake Pads Serviced",  description: "Pads cleaned, greased at contact points, wear measured." },
  { name: "Car Scanning",               description: "Full OBD-II scan for stored + pending fault codes." },
];

// Heuristic icon picker — matches loosely against the live item name.
// Falls back to a generic wrench so any new CRM-side item still renders.
function pickIcon(name = "") {
  const n = name.toLowerCase();
  if (n.includes("oil filter")) return Filter;
  if (n.includes("oil"))        return Droplet;
  if (n.includes("inspect"))    return ClipboardList;
  if (n.includes("ac") || n.includes("air"))      return Wind;
  if (n.includes("brake"))      return Disc;
  if (n.includes("battery"))    return Battery;
  if (n.includes("fluid") || n.includes("top"))   return Gauge;
  if (n.includes("wash") || n.includes("vacuum") || n.includes("clean")) return Sparkles;
  return Wrench;
}

const WhatsIncluded = () => {
  const { pkg } = useCarServicePackage();
  const liveItems =
    Array.isArray(pkg?.items) && pkg.items.length > 0 ? pkg.items : null;
  const items = (liveItems || FALLBACK_ITEMS).slice(0, 8);

  return (
    <section
      className="py-20 lg:py-28 bg-white border-t border-slate-200"
      data-testid="whats-included-section"
    >
      <div className={classes.container}>
        <div className="max-w-2xl mb-12">
          <span className={classes.overline}>Every Service Includes</span>
          <h2 className={`${classes.h2} mt-3`}>
            Eight things we do, every time.<br />
            <span className="text-slate-500">No surprise add-ons.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden">
          {items.map((it, i) => {
            const Icon = pickIcon(it.name);
            return (
              <motion.div
                key={it.name + i}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.35, delay: (i % 4) * 0.05 }}
                className="bg-white hover:bg-slate-50 transition-colors p-6 lg:p-7"
                data-testid={`included-item-${i}`}
              >
                <div className="w-10 h-10 rounded-md bg-blue-50 border border-blue-100 inline-flex items-center justify-center mb-4">
                  <Icon className="h-5 w-5 text-blue-600" strokeWidth={2.2} />
                </div>
                <div className="text-base font-bold text-slate-900 mb-1.5">
                  {it.name}
                </div>
                <div className="text-sm text-slate-600 leading-relaxed">
                  {it.description}
                </div>
              </motion.div>
            );
          })}
        </div>

        <p className="mt-6 text-sm text-slate-500 max-w-2xl">
          <strong className="text-slate-700">Heads up:</strong> Brake pad replacement,
          clutch/timing belt replacement, and battery purchase are quoted separately —
          only if needed, and never without your approval.
        </p>
      </div>
    </section>
  );
};

export default WhatsIncluded;
